'use client';

import { useCallback, useMemo, useState } from 'react';

import type { AttractionBlockData } from '@zoomit/editor';
import { CallCalling, Clock, Instagram, Link, LocationPin, Sms, Telegram, Ticket } from '@zoomit/icons';
import {
  Box,
  cn,
  digitsEnToFa,
  ensureHTTP,
  Flex,
  getHostName,
  getUrlLastSegment,
  Image,
  Skeleton,
  Spacing,
} from '@zoomit/primitives';
import { BaseMediaUrl } from '@zoomit/web-kit';
import { Button } from '@zoomit/zoomit-ds';

import type { AttractionContactInfo } from '../../../types/attraction';
import { AttractionContactInfoPopover } from './AttractionContactInfoPopover';
import { AttractionWorkingHoursPopover } from './AttractionWorkingHoursPopover';
import MapLink from './MapLink';

type Props = {
  blockData: AttractionBlockData;
};

const AttractionBlock = (props: Props) => {
  const { blockData } = props;
  const [isWorkHourOpen, setIsWorkHourOpen] = useState<boolean>(false);
  const [isContactInfoOpen, setIsContactInfoOpen] = useState<boolean>(false);

  const handleToggleWorkingHours = useCallback(() => {
    setIsWorkHourOpen((state) => !state);
  }, []);

  const handleToggleContactInfo = useCallback(() => {
    setIsContactInfoOpen((state) => !state);
  }, []);

  const attractionContactInfo = useMemo(() => {
    const res: AttractionContactInfo[] = [];

    if (blockData.phoneNumber) {
      res.push({
        title: 'تلفن',
        value: digitsEnToFa(blockData.phoneNumber),
        href: 'tel:' + blockData.phoneNumber,
        icon: <CallCalling size="sm" />,
      });
    }

    if (blockData.instagramUrl) {
      const urlLastSeg = getUrlLastSegment(blockData.instagramUrl);
      res.push({
        title: 'اینستاگرام',
        value: urlLastSeg ? '@' + urlLastSeg : blockData.instagramUrl,
        href: ensureHTTP(blockData.instagramUrl),
        icon: <Instagram size="sm" />,
      });
    }

    if (blockData.telegramUrl) {
      const urlLastSeg = getUrlLastSegment(blockData.telegramUrl);
      res.push({
        title: 'تلگرام',
        value: urlLastSeg ? '@' + urlLastSeg : blockData.telegramUrl,
        href: ensureHTTP(blockData.telegramUrl),
        icon: <Telegram size="md" strokeWidth={'8px'} />,
      });
    }

    if (blockData.websiteUrl) {
      const hostName = getHostName(blockData.websiteUrl);
      hostName &&
        res.push({
          title: 'وب‌سایت',
          value: hostName,
          href: ensureHTTP(blockData.websiteUrl),
          icon: <Link size="md" className="-m-0.5" />,
        });
    }

    if (blockData.email) {
      res.push({
        title: 'ایمیل',
        value: blockData.email,
        href: 'mailto:' + blockData.email,
        icon: <Sms size="sm" />,
      });
    }

    return res;
  }, [
    blockData.email,
    blockData.instagramUrl,
    blockData.phoneNumber,
    blockData.telegramUrl,
    blockData.websiteUrl,
  ]);

  return (
    <>
      <Box className="text-body text-text border-grey6 rounded-md border p-4">
        <Flex
          role="section"
          className="flex-col-reverse !flex-nowrap gap-4 leading-7 md:flex-row"
          justifyContent="space-between"
          gap={Spacing.x4}
        >
          <Box>
            <div className="text-h5">{blockData.title}</div>
            <ul className="mt-2 flex flex-col gap-2">
              {blockData.estimatedVisitTime && (
                <li className="flex gap-2" aria-label="زمان بازدید">
                  <Clock size="sm" className="mt-2 shrink-0" />
                  مدت زمان بازدید از این جاذبه: {blockData.estimatedVisitTime}
                </li>
              )}
              {blockData.freeVisit && (
                <li className="flex shrink-0 gap-2">
                  <Ticket size="sm" className="mt-2 shrink-0" />
                  بازدید از این جاذبه رایگان است
                </li>
              )}
              {blockData.address && (
                <li className="flex gap-2" aria-label="آدرس">
                  <LocationPin size="sm" className="mt-2 shrink-0" />
                  <address className="not-italic">{blockData.address}</address>
                </li>
              )}
            </ul>
          </Box>
          {blockData.image ? (
            <Image
              src={`${BaseMediaUrl}/${blockData.image?.id}`}
              width={300}
              height={300}
              className="w-full rounded-md object-fill md:max-w-80"
              alt={blockData.image?.alt ?? blockData.title!}
              blurDataURL={blockData.image.preview}
            />
          ) : (
            <Skeleton width={300} height={170} />
          )}
        </Flex>
        <Flex
          justifyContent="space-between"
          className="border-grey6 mt-4 flex-col gap-3 border-t border-solid pt-4 md:flex-row"
        >
          {blockData.location && (
            <MapLink
              label={blockData.title}
              latitude={blockData.location?.latitude}
              longitude={blockData.location?.longitude}
            />
          )}

          <Flex
            role="group"
            flexWrap="nowrap"
            alignItems="center"
            className="flex-col md:flex-row"
            gap={Spacing.x3}
          >
            <Button
              variant="outline"
              className={cn('!md:w-fit !w-full overflow-hidden text-nowrap text-ellipsis', {
                hidden: !blockData.openingHours,
              })}
              spacing={{ py: 'x2' }}
              onClick={handleToggleWorkingHours}
            >
              مشاهده ساعت کاری
            </Button>
            <Button
              variant="outline"
              className={cn('!md:w-fit !w-full', {
                hidden: attractionContactInfo.length === 0,
              })}
              onClick={handleToggleContactInfo}
            >
              اطلاعات تماس
            </Button>
          </Flex>
        </Flex>
      </Box>
      {blockData.openingHours && (
        <AttractionWorkingHoursPopover
          data={blockData.openingHours}
          isOpen={isWorkHourOpen}
          onClose={handleToggleWorkingHours}
        />
      )}
      {attractionContactInfo.length !== 0 && (
        <AttractionContactInfoPopover
          data={attractionContactInfo}
          isOpen={isContactInfoOpen}
          onClose={handleToggleContactInfo}
        />
      )}
    </>
  );
};

export default AttractionBlock;
